import { RichText } from "@components/cms/rich-text-renderer/RichText";
import type { CheckboxProps } from "@ecco/ui/checkbox";
import { Checkbox } from "@ecco/ui/checkbox";
import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import type { Document } from "@contentful/rich-text-types";
import type { ComponentProps, InputHTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { forwardRef } from "react";
import { Text } from "@ecco/ui/text";

// `value` is redeclared as a boolean below - a checkbox's value is whether it's checked, not the
// string/array/number the native input attribute allows, so that key is dropped from the base type.
type CheckboxFieldFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value"> &
    PropsWithChildren<{
        value?: boolean;
        error?: string;
        label: Document | string | ReactNode;
        testId?: string;
        required?: boolean;
        isInvalid?: boolean;
        isSuccess?: boolean;
        isReadOnly?: boolean;
        // This causes a clash because InputHTMLAttributes also has size defined which doesn't match the Input props.
        size?: ComponentProps<typeof Checkbox>["size"];
    }>;

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldFieldProps>(
    (
        {
            value,
            error,
            label,
            testId,
            required = false,
            isInvalid,
            isSuccess,
            isReadOnly,
            size,
            children,
            "aria-invalid": ariaInvalid,
            ...rest
        },
        ref
    ) => {
        const getLabel = () => {
            if (typeof label === "string") {
                return label;
            }
            if (typeof label === "object" && "nodeType" in label) {
                return <RichText data={label} />;
            }
            return <Text as="span">{label}</Text>;
        };
        return (
            <FormControl
                isInvalid={isInvalid}
                isRequired={required}
                isReadOnly={isReadOnly}
                isSuccess={isSuccess}
            >
                <Checkbox
                    colorScheme="black"
                    data-testid={testId}
                    isChecked={value}
                    ref={ref}
                    // Chakra v2 has compatibility issues with InputHTMLAttributes. Chakra thinks aria-invalid is a boolean or undefined.
                    aria-invalid={ariaInvalid as CheckboxProps["aria-invalid"]}
                    {...rest}
                >
                    {getLabel()}
                </Checkbox>
                {children}
                <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>
        );
    }
);

CheckboxField.displayName = "CheckboxField";
